import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useAuth } from '../contexts/AuthContext';
import { useAdminPanel } from '../contexts/AdminPanelContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Shield,
  ShieldCheck,
  Users,
  HardDrive,
  Send,
  Settings,
  Sliders,
  UserCog,
  Radio,
  ScrollText,
  ChevronDown,
} from 'lucide-react';
import { UserManagement } from './admin/UserManagement';
import { DeviceManagement } from './admin/DeviceManagement';
import { FeatureManagement } from './admin/FeatureManagement';
import { GlobalSettings } from './admin/GlobalSettings';
import { CommandCenter } from './admin/CommandCenter';
import { ImpersonationAudit } from './admin/ImpersonationAudit';
import { BeaconManagement } from './admin/BeaconManagement';
import GlobalAuditLog from './admin/GlobalAuditLog';

export const AdminPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSubpageEnabled } = useFeatureFlags();
  const { user, api } = useAuth();
  const { persistedActiveTab, setPersistedActiveTab } = useAdminPanel();
  const [isolationAudit, setIsolationAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  
  // Check which admin subpages are enabled — controlled purely by feature flags + privileges
  // Role restrictions are enforced by the backend /api/features endpoint
  const hasUserManagement = isSubpageEnabled('admin_user_management');
  const hasDeviceManagement = isSubpageEnabled('admin_device_management');
  const hasFeatureManagement = isSubpageEnabled('admin_feature_management');
  const hasSystemSettings = isSubpageEnabled('admin_system_settings');
  const hasCommands = isSubpageEnabled('admin_commands');
  const hasIsolationAudit = user?.role === 'super_admin' || (user?.role === 'admin' && isSubpageEnabled('admin_tenant_isolation_audit'));
  const hasImpersonationAudit = user?.role === 'super_admin' || (user?.role === 'admin' && isSubpageEnabled('admin_impersonation_audit'));
  const hasGlobalAuditLog = user?.role === 'super_admin' || (user?.role === 'admin' && isSubpageEnabled('admin_global_audit_log'));
  const hasBeaconManagement = user?.role === 'super_admin' || (user?.role === 'admin' && isSubpageEnabled('admin_beacon_management'));
  
  // SECURITY FIX: Return null if no subpages enabled
  const getDefaultTab = () => {
    if (hasUserManagement) return 'users';
    if (hasDeviceManagement) return 'devices';
    if (hasCommands) return 'commands';
    if (hasIsolationAudit) return 'isolation-audit';
    if (hasImpersonationAudit) return 'impersonation-audit';
    if (hasGlobalAuditLog) return 'audit-log';
    if (hasBeaconManagement) return 'beacon-management';
    if (hasFeatureManagement) return 'features';
    if (hasSystemSettings) return 'system';
    return null;
  };

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const getRequestedTab = () => {
    const allowedTabs = {
      users: hasUserManagement,
      devices: hasDeviceManagement,
      commands: hasCommands,
      'isolation-audit': hasIsolationAudit,
      'impersonation-audit': hasImpersonationAudit,
      'audit-log': hasGlobalAuditLog,
      'beacon-management': hasBeaconManagement,
      features: hasFeatureManagement,
      system: hasSystemSettings,
    };
    return allowedTabs[requestedTab] ? requestedTab : null;
  };
  
  const [mobileTabsOpen, setMobileTabsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const requested = getRequestedTab();
    if (requested) return requested;
    const allowedTabs = {
      users: hasUserManagement,
      devices: hasDeviceManagement,
      commands: hasCommands,
      'isolation-audit': hasIsolationAudit,
      'impersonation-audit': hasImpersonationAudit,
      'audit-log': hasGlobalAuditLog,
      'beacon-management': hasBeaconManagement,
      features: hasFeatureManagement,
      system: hasSystemSettings,
    };
    if (persistedActiveTab && allowedTabs[persistedActiveTab]) return persistedActiveTab;
    return getDefaultTab();
  });

  // Mirror activeTab back into persistence (URL still wins on initial load)
  useEffect(() => {
    if (activeTab && activeTab !== persistedActiveTab) setPersistedActiveTab(activeTab);
  }, [activeTab, persistedActiveTab, setPersistedActiveTab]);
  
  // SECURITY FIX: Update activeTab when feature flags load
  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && activeTab !== requested) {
      setActiveTab(requested);
      return;
    }
    const allowedTabs = {
      users: hasUserManagement,
      devices: hasDeviceManagement,
      commands: hasCommands,
      'isolation-audit': hasIsolationAudit,
      'impersonation-audit': hasImpersonationAudit,
      'audit-log': hasGlobalAuditLog,
      'beacon-management': hasBeaconManagement,
      features: hasFeatureManagement,
      system: hasSystemSettings,
    };
    if (!activeTab || allowedTabs[activeTab] !== true) {
      const defaultTab = getDefaultTab();
      if (defaultTab) setActiveTab(defaultTab);
    }
  // getRequestedTab/getDefaultTab are pure readers of `requestedTab` and the has*
  // feature flags — all already listed in this dependency array — so the effect
  // re-runs correctly. Not adding the helpers avoids a useCallback chain for no gain.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab, activeTab, hasUserManagement, hasDeviceManagement, hasFeatureManagement, hasSystemSettings, hasCommands, hasIsolationAudit, hasImpersonationAudit, hasGlobalAuditLog, hasBeaconManagement]);

  // Deep-link tab change handler: updates state + URL `?tab=` (replace mode so
  // tab clicks don't pollute browser history). Feature-gate enforced.
  const handleAdminTabChange = useCallback((tab) => {
    const allowedTabs = {
      users: hasUserManagement,
      devices: hasDeviceManagement,
      commands: hasCommands,
      'isolation-audit': hasIsolationAudit,
      'impersonation-audit': hasImpersonationAudit,
      'audit-log': hasGlobalAuditLog,
      'beacon-management': hasBeaconManagement,
      features: hasFeatureManagement,
      system: hasSystemSettings,
    };
    if (!allowedTabs[tab]) return;
    setActiveTab(tab);
    navigate(`/admin-panel?tab=${tab}`, { replace: true });
  }, [hasUserManagement, hasDeviceManagement, hasCommands, hasIsolationAudit, hasImpersonationAudit, hasGlobalAuditLog, hasBeaconManagement, hasFeatureManagement, hasSystemSettings, navigate]);

  const fetchIsolationAudit = async () => {
    if (!api || !hasIsolationAudit) return;
    try {
      setAuditLoading(true);
      const response = await api.get('/admin/tenant-isolation-audit');
      setIsolationAudit(response.data);
    } catch (error) {
      console.error('Failed to load tenant isolation audit:', error);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'isolation-audit') {
      fetchIsolationAudit();
    }
  // fetchIsolationAudit reads only `api` and `hasIsolationAudit`, both already deps;
  // it loads the audit when that tab opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, api, hasIsolationAudit]);

  return (
    <div className="space-y-6" data-testid="admin-panel-page">
      {/* Page Header */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-green-500/10">
          <Shield className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin Panel</h1>
          <p className="text-sm text-muted-foreground">System administration and configuration</p>
        </div>
        <Badge className="ml-auto bg-green-600">Admin Access</Badge>
      </div>

      {/* Mobile sub-navigation — dropdown menu (mirrors the main app's mobile
          nav pattern). Hidden on ≥sm where the pill buttons show instead. */}
      {(() => {
        const adminTabDefs = [
          { id: 'users', label: 'User Management', icon: Users, allowed: hasUserManagement },
          { id: 'devices', label: 'Device Management', icon: HardDrive, allowed: hasDeviceManagement },
          { id: 'commands', label: 'Commands', icon: Send, allowed: hasCommands },
          { id: 'features', label: 'Feature Management', icon: Sliders, allowed: hasFeatureManagement },
          { id: 'system', label: 'System Settings', icon: Settings, allowed: hasSystemSettings },
          { id: 'isolation-audit', label: 'Tenant Isolation Audit', icon: ShieldCheck, allowed: hasIsolationAudit },
          { id: 'impersonation-audit', label: 'Impersonation Audit', icon: UserCog, allowed: hasImpersonationAudit },
          { id: 'audit-log', label: 'Audit Log', icon: ScrollText, allowed: hasGlobalAuditLog },
          { id: 'beacon-management', label: 'Beacon Management', icon: Radio, allowed: hasBeaconManagement },
        ].filter(t => t.allowed);
        const activeDef = adminTabDefs.find(t => t.id === activeTab);
        const ActiveIcon = activeDef?.icon || Shield;
        return (
          <div className="relative sm:hidden" data-testid="admin-tabs-mobile-dropdown">
            <button
              type="button"
              onClick={() => setMobileTabsOpen(v => !v)}
              aria-expanded={mobileTabsOpen}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-card px-4 py-3 text-base font-semibold text-foreground shadow-sm"
              data-testid="admin-tabs-mobile-trigger"
            >
              <span className="flex items-center gap-3 min-w-0">
                <ActiveIcon className="w-5 h-5 shrink-0 text-[#2563eb]" />
                <span className="truncate">{activeDef?.label || 'Select Section'}</span>
              </span>
              <ChevronDown className={`w-5 h-5 shrink-0 text-muted-foreground transition-transform ${mobileTabsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileTabsOpen && (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-[1040] bg-black/10"
                  onClick={() => setMobileTabsOpen(false)}
                  aria-label="Close section menu"
                  data-testid="admin-tabs-mobile-backdrop"
                />
                <div className="absolute left-0 right-0 top-full z-[1090] mt-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 shadow-2xl space-y-1" data-testid="admin-tabs-mobile-menu">
                  {adminTabDefs.map(t => {
                    const isActive = activeTab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => { handleAdminTabChange(t.id); setMobileTabsOpen(false); }}
                        className={`flex w-full min-h-[48px] items-center gap-3.5 px-4 py-3 rounded-xl text-base font-semibold transition-colors ${isActive
                          ? 'bg-[#2563eb] text-white'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                        data-testid={`admin-tabs-mobile-item-${t.id}`}
                      >
                        <t.icon className="w-5 h-5 shrink-0" />
                        <span className="truncate">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Sub-navigation Tabs — pill buttons on ≥sm screens. */}
      <div className="hidden sm:flex gap-2 flex-wrap" data-testid="admin-tabs-row">
        {hasUserManagement && (
          <Button
            onClick={() => handleAdminTabChange('users')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'users' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-users"
          >
            <Users className="w-4 h-4" />
            User Management
          </Button>
        )}
        {hasDeviceManagement && (
          <Button
            onClick={() => handleAdminTabChange('devices')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'devices' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-devices"
          >
            <HardDrive className="w-4 h-4" />
            Device Management
          </Button>
        )}
        {hasCommands && (
          <Button
            onClick={() => handleAdminTabChange('commands')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'commands' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-commands"
          >
            <Send className="w-4 h-4" />
            Commands
          </Button>
        )}
        {hasFeatureManagement && (
          <Button
            onClick={() => handleAdminTabChange('features')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'features' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-features"
          >
            <Sliders className="w-4 h-4" />
            Feature Management
          </Button>
        )}
        {hasSystemSettings && (
          <Button
            onClick={() => handleAdminTabChange('system')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'system' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-system"
          >
            <Settings className="w-4 h-4" />
            System Settings
          </Button>
        )}
        {hasIsolationAudit && (
          <Button
            onClick={() => handleAdminTabChange('isolation-audit')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'isolation-audit'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-tenant-isolation-audit"
          >
            <ShieldCheck className="w-4 h-4" />
            Tenant Isolation Audit
          </Button>
        )}
        {hasImpersonationAudit && (
          <Button
            onClick={() => handleAdminTabChange('impersonation-audit')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'impersonation-audit'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-impersonation-audit"
          >
            <UserCog className="w-4 h-4" />
            Impersonation Audit
          </Button>
        )}
        {hasGlobalAuditLog && (
          <Button
            onClick={() => handleAdminTabChange('audit-log')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'audit-log'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-audit-log"
          >
            <ScrollText className="w-4 h-4" />
            Audit Log
          </Button>
        )}
        {hasBeaconManagement && (
          <Button
            onClick={() => handleAdminTabChange('beacon-management')}
            className={`gap-2 min-w-[210px] justify-center ${activeTab === 'beacon-management'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-beacon-management"
          >
            <Radio className="w-4 h-4" />
            Beacon Management
          </Button>
        )}
      </div>

      {/* No Access Message - SECURITY FIX */}
      {!activeTab && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">No Access</p>
            <p className="text-sm text-muted-foreground mt-2">You don't have permission to access any admin sections.</p>
            <p className="text-xs text-muted-foreground mt-4">Contact your super administrator.</p>
          </CardContent>
        </Card>
      )}

      {/* Render Active Subpage - SECURITY FIX: Add permission checks */}
      {activeTab === 'users' && hasUserManagement && <UserManagement />}
      {activeTab === 'devices' && hasDeviceManagement && <DeviceManagement />}
      {activeTab === 'features' && hasFeatureManagement && <FeatureManagement />}
      {activeTab === 'system' && hasSystemSettings && <GlobalSettings />}
      {activeTab === 'commands' && hasCommands && <CommandCenter />}
      {activeTab === 'isolation-audit' && hasIsolationAudit && (
        <div className="space-y-4" data-testid="tenant-isolation-audit-panel">
          {/* Page header — matches User/Device/Beacon Management styling. */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#2563eb]/10">
              <ShieldCheck className="w-6 h-6 text-[#2563eb]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="tenant-isolation-audit-title">Tenant Isolation Audit</h1>
              <p className="text-sm text-muted-foreground">Current visibility counts for this signed-in account</p>
            </div>
          </div>

          <Card>
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center justify-end gap-4">
                <Button variant="outline" onClick={fetchIsolationAudit} disabled={auditLoading} data-testid="tenant-isolation-audit-refresh-button">
                  {auditLoading ? 'Refreshing…' : 'Refresh'}
                </Button>
              </div>

            {isolationAudit?.audited_user && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-md border bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs text-muted-foreground">User</p>
                  <p className="font-medium" data-testid="tenant-isolation-audit-user">{isolationAudit.audited_user.username || isolationAudit.audited_user.email}</p>
                </div>
                <div className="p-3 rounded-md border bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs text-muted-foreground">Role</p>
                  <p className="font-medium capitalize" data-testid="tenant-isolation-audit-role">{String(isolationAudit.audited_user.role || '').replace('_', ' ')}</p>
                </div>
                <div className="p-3 rounded-md border bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs text-muted-foreground">Users visible</p>
                  <p className="font-medium" data-testid="tenant-isolation-audit-users-count">{isolationAudit.counts?.users ?? 0}</p>
                </div>
                <div className="p-3 rounded-md border bg-slate-50 dark:bg-slate-900/40">
                  <p className="text-xs text-muted-foreground">Devices visible</p>
                  <p className="font-medium" data-testid="tenant-isolation-audit-devices-count">{isolationAudit.counts?.devices ?? 0}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {(isolationAudit?.pages || []).map((row) => (
                <div key={row.page} className="p-4 rounded-md border bg-white dark:bg-slate-950" data-testid={`tenant-isolation-audit-page-${row.page.toLowerCase()}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">{row.page}</p>
                    <Badge className="bg-[#2563eb]">{row.count}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">{row.scope}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </div>
      )}
      {activeTab === 'impersonation-audit' && hasImpersonationAudit && <ImpersonationAudit />}
      {activeTab === 'audit-log' && hasGlobalAuditLog && <GlobalAuditLog />}
      {activeTab === 'beacon-management' && hasBeaconManagement && <BeaconManagement />}
    </div>
  );
};
